import React, { useRef, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  OrbitControls,
  Environment,
  Lightformer,
  Sphere,
} from "@react-three/drei";
import { EffectComposer, Bloom, GodRays } from "@react-three/postprocessing";
import * as THREE from "three";

// Soft Easing
const ease = (t) => t * t * (3 - 2 * t);

// Jelly wobble
const wobble = (i, t) =>
  Math.sin(i * 0.15 + t * 1.2) * 0.02 +
  Math.cos(i * 0.09 + t * 0.4) * 0.02;

// Shape math
const toSphere = (d) => d.clone();
const toCube = (d) => {
  const m = Math.max(Math.abs(d.x), Math.abs(d.y), Math.abs(d.z));
  return new THREE.Vector3(d.x / m, d.y / m, d.z / m);
};
const toTorus = (d) => {
  const t = Math.atan2(d.z, d.x);
  const p = Math.acos(THREE.MathUtils.clamp(d.y, -1, 1));
  const R = 0.8,
    r = 0.35;
  return new THREE.Vector3(
    (R + r * Math.cos(p)) * Math.cos(t),
    r * Math.sin(p),
    (R + r * Math.cos(p)) * Math.sin(t)
  );
};
const toCrystal = (d) =>
  d.clone().multiply(
    new THREE.Vector3(
      1 + 0.8 * Math.sin(d.x * 6),
      1 + 0.8 * Math.cos(d.y * 6),
      1 + 0.8 * Math.sin(d.z * 6)
    )
  );

function GalaxyDust() {
  const ref = useRef();
  const count = 1200;

  const pos = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = THREE.MathUtils.randFloat(1.2, 5);
      const a = Math.random() * Math.PI * 2;
      const h = THREE.MathUtils.randFloatSpread(1.5);
      arr[i * 3] = Math.cos(a) * r;
      arr[i * 3 + 1] = h;
      arr[i * 3 + 2] = Math.sin(a) * r;
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    ref.current.rotation.y = clock.getElapsedTime() * 0.006;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={pos}
          count={count}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.015}
        color="#bbd2ff"
        transparent
        opacity={0.5}
      />
    </points>
  );
}

function AuraRibbon() {
  const points = [];
  for (let i = 0; i < 100; i++) {
    points.push(new THREE.Vector3(Math.sin(i * 0.2) * 0.6, i * 0.02 - 1, 0));
  }

  return (
    <mesh>
      <tubeGeometry args={[new THREE.CatmullRomCurve3(points), 200, 0.05, 12, false]} />
      <meshBasicMaterial color="#9ac7ff" transparent opacity={0.25} />
    </mesh>
  );
}

function MorphingCore() {
  const mesh = useRef();
  const { clock } = useThree();

  const { base, sphere, cube, torus, crystal } = useMemo(() => {
    const geo = new THREE.IcosahedronGeometry(1, 6);
    const count = geo.attributes.position.count;

    const make = (modifier) => {
      const a = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        const d = new THREE.Vector3()
          .fromBufferAttribute(geo.attributes.position, i)
          .normalize();

        const p = modifier(d);
        a[i * 3] = p.x;
        a[i * 3 + 1] = p.y;
        a[i * 3 + 2] = p.z;
      }
      return a;
    };

    return {
      base: make((d) => d),
      sphere: make(toSphere),
      cube: make(toCube),
      torus: make(toTorus),
      crystal: make(toCrystal),
    };
  }, []);

  const shapes = ["sphere", "cube", "torus", "crystal"];
  const map = { sphere, cube, torus, crystal };

  const state = useRef({ curr: 0, next: 1, t: 0, duration: 5 });

  const geom = useMemo(
    () =>
      new THREE.BufferGeometry().setAttribute(
        "position",
        new THREE.BufferAttribute(base, 3)
      ),
    [base]
  );

  useFrame((_, delta) => {
    const t = clock.getElapsedTime();
    const st = state.current;
    const pos = geom.attributes.position;

    st.t += delta / st.duration;
    const p = ease(Math.min(1, st.t));

    const A = map[shapes[st.curr]];
    const B = map[shapes[st.next]];

    for (let i = 0; i < pos.count; i++) {
      const i3 = i * 3;
      const jelly = wobble(i, t);

      pos.array[i3] = THREE.MathUtils.lerp(A[i3], B[i3], p) + jelly;
      pos.array[i3 + 1] = THREE.MathUtils.lerp(A[i3 + 1], B[i3 + 1], p) + jelly;
      pos.array[i3 + 2] = THREE.MathUtils.lerp(A[i3 + 2], B[i3 + 2], p) + jelly;
    }

    pos.needsUpdate = true;

    // Breathing scale
    const s = 1 + Math.sin(t * 1.1) * 0.06;
    mesh.current.scale.set(s, s, s);

    // Smooth ambient rotation
    mesh.current.rotation.y += 0.003;
    mesh.current.rotation.x += 0.0015;

    if (st.t >= 1) {
      st.curr = st.next;
      st.next = (st.next + 1) % shapes.length;
      st.t = 0;
    }
  });

  return (
    <mesh ref={mesh} geometry={geom}>
      <meshStandardMaterial
        color="#bbd7ff"
        roughness={0.2}
        metalness={0.35}
        emissive="#8bb0ff"
        emissiveIntensity={0.7}
      />
    </mesh>
  );
}

export default function ASMRMorphingUniverse() {
  return (
    <div style={{ width: "100%", height: "100vh", background: "#03050b" }}>
      <Canvas camera={{ position: [0, 0, 4], fov: 45 }}>
        {/* Lights */}
        <ambientLight intensity={0.4} />
        <pointLight intensity={1} position={[4, 2, 4]} color="#9fc3ff" />
        <pointLight intensity={0.8} position={[-3, -3, -4]} color="#ffd2f3" />

        {/* Background */}
        <GalaxyDust />
        <AuraRibbon />

        {/* Morphing core */}
        <MorphingCore />

        {/* HDR environment */}
        <Environment preset="sunset" />

        {/* Effects */}
        <EffectComposer>
          <Bloom intensity={1.5} luminanceThreshold={0} luminanceSmoothing={0.7} />
        </EffectComposer>

        {/* Locked to avoid interaction (pure ASMR) */}
        <OrbitControls
          enablePan={false}
          enableZoom={false}
          enableRotate={false}
        />
      </Canvas>
    </div>
  );
}
